'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ShoppingCartIcon, XMarkIcon, CheckCircleIcon, FunnelIcon } from '@heroicons/react/24/outline';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Product {
  id: string;
  name: string;
  price: number;
  period: string;
  desc: string;
  displayPrice?: string;
  features: string[];
  tag?: string;
  modules: string[];
  sizes: string[];
  industries: string[];
}

const products: Product[] = [
  {
    id: 'starter',
    name: 'MANGQASHA IT SOLUTIONS Starter',
    price: 4825.74,
    displayPrice: 'R4 825,74',
    period: '/mo',
    desc: 'Core ERP for small businesses. Finance, inventory, and HR in one platform.',
    features: ['Finance & Accounting', 'Inventory Management', 'HR & Payroll', '5 user seats', 'Email support', '30-day free trial'],
    modules: ['Finance', 'Inventory', 'HR'],
    sizes: ['1–50'],
    industries: ['Retail', 'Services', 'Distribution'],
  },
  {
    id: 'professional',
    name: 'MANGQASHA IT SOLUTIONS Professional',
    price: 11281.47,
    displayPrice: 'R11 281,47',
    period: '/mo',
    desc: 'Full ERP suite with CRM, supply chain, and social selling for growing teams.',
    tag: 'Most Popular',
    features: ['All Starter modules', 'CRM & Sales', 'Supply Chain', 'Social Selling (FB + IG)', '25 user seats', 'Priority support'],
    modules: ['Finance', 'Inventory', 'HR', 'CRM', 'Supply Chain'],
    sizes: ['51–200'],
    industries: ['Retail', 'Manufacturing', 'Distribution', 'Services'],
  },
  {
    id: 'enterprise',
    name: 'MANGQASHA IT SOLUTIONS Enterprise',
    price: 0,
    period: 'custom',
    desc: 'Complete ERP with manufacturing, unlimited users, and dedicated support.',
    features: ['All Professional modules', 'Manufacturing', 'Unlimited users', 'Custom integrations', 'Dedicated CSM', 'SLA guarantee'],
    modules: ['Finance', 'Inventory', 'HR', 'CRM', 'Supply Chain', 'Manufacturing'],
    sizes: ['201–1000', '1000+'],
    industries: ['Manufacturing', 'Distribution', 'Retail', 'Services'],
  },
  {
    id: 'social-addon',
    name: 'Social Selling Add-on',
    price: 1597.80,
    displayPrice: 'R1 597,80',
    period: '/mo',
    desc: 'Add Facebook Shop and Instagram Shopping to any existing ERP plan.',
    features: ['Facebook Shop sync', 'Instagram Shopping', 'Social order management', 'Social analytics dashboard', 'DM automation'],
    modules: ['CRM'],
    sizes: ['1–50', '51–200'],
    industries: ['Retail', 'Services'],
  },
];

interface CartItem {
  product: Product;
  qty: number;
}

export default function StorePage() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [filterModule, setFilterModule] = useState('All');
  const [filterSize, setFilterSize] = useState('All');

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) return prev.map((i) => i.product.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product, qty: 1 }];
    });
    setCartOpen(true);
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== id));
  };

  const subtotal = cart.reduce((sum, i) => sum + (i.product.price * i.qty), 0);
  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  const allModules = ['All', 'Finance', 'Inventory', 'HR', 'CRM', 'Supply Chain', 'Manufacturing'];
  const allSizes = ['All', '1–50', '51–200', '201–1000', '1000+'];

  const filtered = products.filter((p) => {
    const modMatch = filterModule === 'All' || p.modules.includes(filterModule);
    const sizeMatch = filterSize === 'All' || p.sizes.includes(filterSize);
    return modMatch && sizeMatch;
  });

  return (
    <div className="min-h-screen bg-[#0F1B2D]">
      <Navbar />

      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start justify-between">
          <div>
            <p className="section-label mb-2">ERP Store</p>
            <h1 className="text-3xl font-extrabold text-white mb-2">Choose Your ERP Package</h1>
            <p className="text-[#94A3B8] text-sm">All plans include a 30-day free trial. Add to cart to get started.</p>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white hover:border-[#00C9A7] transition-colors"
          >
            <ShoppingCartIcon className="w-5 h-5" />
            Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#00C9A7] text-[#0F1B2D] text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-[#64748B]" />
            <span className="text-xs text-[#64748B] font-semibold">Filter by:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-[#64748B] self-center">Module:</span>
            {allModules.map((m) => (
              <button
                key={m}
                onClick={() => setFilterModule(m)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  filterModule === m ? 'bg-[#00C9A7]/10 border-[#00C9A7]/40 text-[#00C9A7]' : 'bg-[#162236] border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7]/30'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-[#64748B] self-center">Size:</span>
            {allSizes.map((s) => (
              <button
                key={s}
                onClick={() => setFilterSize(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  filterSize === s ? 'bg-[#00C9A7]/10 border-[#00C9A7]/40 text-[#00C9A7]' : 'bg-[#162236] border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7]/30'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {filtered.map((product) => (
            <div
              key={product.id}
              className={`bg-[#162236] border rounded-2xl p-6 relative card-hover ${
                product.tag ? 'border-[#00C9A7]/40' : 'border-[#1E3352]'
              }`}
            >
              {product.tag && (
                <div className="absolute top-4 right-4 bg-[#00C9A7] text-[#0F1B2D] text-xs font-bold px-3 py-1 rounded-full">
                  {product.tag}
                </div>
              )}
              <h2 className="text-lg font-bold text-white mb-1 pr-24">{product.name}</h2>
              <p className="text-xs text-[#64748B] mb-4">{product.desc}</p>
              <div className="flex items-baseline gap-1 mb-5">
                {product.price > 0 ? (
                  <>
                    <span className="text-3xl font-extrabold text-white">{product.displayPrice ?? `$${product.price.toLocaleString()}`}</span>
                    <span className="text-sm text-[#64748B]">{product.period}</span>
                  </>
                ) : (
                  <span className="text-2xl font-extrabold text-white">Custom Pricing</span>
                )}
              </div>
              <ul className="space-y-2 mb-6">
                {product.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <CheckCircleIcon className="w-4 h-4 text-[#00C9A7] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                {product.price > 0 ? (
                  <button
                    onClick={() => addToCart(product)}
                    className="flex-1 btn-primary py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
                  >
                    <ShoppingCartIcon className="w-4 h-4" /> Add to Cart
                  </button>
                ) : (
                  <Link href="/book-demo" className="flex-1 btn-primary py-2.5 text-sm font-semibold text-center">
                    Contact Sales
                  </Link>
                )}
                <Link href="/book-demo" className="flex-1 btn-outline py-2.5 text-sm font-semibold text-center">
                  Book Demo
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-sm bg-[#162236] border-l border-[#1E3352] h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E3352]">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <ShoppingCartIcon className="w-5 h-5 text-[#00C9A7]" /> Your Cart
              </h2>
              <button onClick={() => setCartOpen(false)} className="text-[#64748B] hover:text-white">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCartIcon className="w-10 h-10 text-[#1E3352] mx-auto mb-3" />
                  <p className="text-sm text-[#64748B]">Your cart is empty</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.product.id} className="bg-[#0F1B2D] border border-[#1E3352] rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-white">{item.product.name}</p>
                        <p className="text-xs text-[#64748B] mt-0.5">{item.product.period === 'custom' ? 'Custom pricing' : `${item.product.displayPrice ?? `$${item.product.price.toLocaleString()}`}${item.product.period}`}</p>
                      </div>
                      <button onClick={() => removeFromCart(item.product.id)} className="text-[#64748B] hover:text-[#EF4444]">
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="px-5 py-4 border-t border-[#1E3352]">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-[#94A3B8]">Subtotal / month</span>
                  <span className="text-lg font-bold text-white">R{subtotal.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace('.', ',')}</span>
                </div>
                <Link
                  href="/book-demo"
                  className="btn-primary w-full py-3 text-sm font-bold text-center block"
                  onClick={() => setCartOpen(false)}
                >
                  Proceed to Demo Booking
                </Link>
                <p className="text-xs text-[#64748B] text-center mt-2">Finalize pricing with our sales team</p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
